"use client";

import { FaArrowDown, FaBell, FaAngleUp } from "react-icons/fa";
import SocialIcons from "../header/SocialIcons";
import Link from "@/components/ui/Link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { getMenuGroups, MenuGroupItem } from "@/lib/api";


// Simple link helpers
const FooterLink = (
  {
    href = "#",
    external = false,
    children,
  }: {
    href?: string;
    external?: boolean;
    children: React.ReactNode;
  }
) => {
  if (external) {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-FFFFFF hover:opacity-80 transition-opacity text-12-tamil-600"
      >
        {children}
      </Link>
    );
  }

  return (
    <Link href={href} className="text-FFFFFF hover:opacity-80 transition-opacity text-12-tamil-600">
      {children}
    </Link>
  );
};

export default function Footer() {
  const [footerItems, setFooterItems] = useState<MenuGroupItem[]>([]);
  const [footerTopItems, setFooterTopItems] = useState<MenuGroupItem[]>([]);
  const [footerSectionItems, setFooterSectionItems] = useState<MenuGroupItem[]>([]);
  const [footerGroupPages, setFooterGroupPages] = useState<MenuGroupItem[]>([]);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getMenuGroups();
      const footer = data?.["menu-groups"]?.footer?.items ?? [];
      const cleanedFooter = footer.filter((it) => Boolean(it?.title) && Boolean(it?.url || it?.data?.link));

      // Fetch custom-footer-company-pages for top navigation
      const companyPages = data?.["menu-groups"]?.["custom-footer-company-pages"]?.items ?? [];
      const cleanedCompanyPages = companyPages.filter((it) => Boolean(it?.title) && Boolean(it?.url || it?.data?.link));

      // Fetch custom-footer-section-menu for sections grid
      const sectionMenu = data?.["menu-groups"]?.["custom-footer-section-menu"]?.items ?? [];
      const cleanedSectionMenu = sectionMenu.filter((it) => Boolean(it?.title) && Boolean(it?.url || it?.data?.link));

      // Fetch custom-footer-group-pages for right column
      const groupPages = data?.["menu-groups"]?.["custom-footer-group-pages"]?.items ?? [];
      const cleanedGroupPages = groupPages.filter((it) => Boolean(it?.title) && Boolean(it?.url || it?.data?.link));

      if (!cancelled) setFooterItems(cleanedFooter);
      if (!cancelled) setFooterTopItems(cleanedCompanyPages);
      if (!cancelled) setFooterSectionItems(cleanedSectionMenu);
      if (!cancelled) setFooterGroupPages(cleanedGroupPages);
    })();
    return () => { cancelled = true; };
  }, []);

  const resolveFooterHref = (item: MenuGroupItem): { href: string; external: boolean } | null => {
    const raw = item?.url || item?.data?.link;
    if (!raw) return null;

    const toLastSegmentRoute = (pathnameValue: string): string => {
      const cleaned = (pathnameValue || "/").split("?")[0].replace(/\/+$/, "");
      const parts = cleaned.split("/").filter(Boolean);
      const last = parts[parts.length - 1];
      return last ? `/${last}` : "/";
    };

    try {
      const base = typeof window !== "undefined" ? window.location.origin : "https://www.dinamani.com";
      const parsed = new URL(raw, base);
      const isDinamani = parsed.hostname === "www.dinamani.com" || parsed.hostname === "dinamani.com";

      if (isDinamani) {
        return { href: toLastSegmentRoute(parsed.pathname || "/"), external: false };
      }

      return { href: raw, external: true };
    } catch {
      if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return { href: raw, external: true };
      }
      return { href: toLastSegmentRoute(raw), external: false };
    }
  };

  // Resolve full URL for section menu items
  const resolveSectionHref = (item: MenuGroupItem): { href: string; external: boolean } | null => {
    const raw = item?.url || item?.data?.link;
    if (!raw) return null;

    try {
      const base = typeof window !== "undefined" ? window.location.origin : "https://www.dinamani.com";
      const parsed = new URL(raw, base);
      const isDinamani = parsed.hostname === "www.dinamani.com" || parsed.hostname === "dinamani.com";

      if (isDinamani) {
        // Return relative path for internal links
        // parsed.pathname starts with /, so this creates a root-relative path
        return { href: parsed.pathname + parsed.search, external: false };
      }

      return { href: raw, external: true };
    } catch {
      if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return { href: raw, external: true };
      }
      // For relative URLs, usually assume internal
      const relativeUrl = raw.startsWith("/") ? raw : "/" + raw;
      return { href: relativeUrl, external: false };
    }
  };




  return (
    <footer className="w-full bg-012061 text-FFFFFF relative">
      {/* Outer left/right rails to match page framing */}
      <div className="border-x border-2D497E">
        <div className="max-w-[1245px] w-full mx-auto">
          {/* Main 3-column area */}
          <div className="relative grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)_300px] lg:gap-0 border-b lg:border-y border-2D497E">
            {/* Left column */}
            <div className="pr-0 lg:border-l border-2D497E">
              <div className="flex items-center justify-center p-5">
                <Image
                  src="https://media.dinamani.com/dinamani/2026-02-27/oma80fx0/dinamanilogodarkmode.png"
                  alt="Dinamani"
                  width={180}
                  height={78}
                  className="w-[180px] h-auto"
                  loading="lazy"
                />
              </div>
              <div className="flex items-center gap-2 text-FFFFFF p-5 border-t border-2D497E">
                {/* <FaArrowDown size={16} aria-hidden /> */}
                <span className="text-12-tamil-900">தினமணி இணையதளத்தை பின்தொடர</span>
              </div>

              <div className="p-5 border-t border-2D497E">
                <SocialIcons useGrid={false} showAppIcons={false} />
              </div>

              <div className="flex items-center gap-2 text-FFFFFF p-5 border-t border-2D497E">
                {/* <FaArrowDown size={16} aria-hidden /> */}
                <span className="text-12-tamil-900">செயலிகளை பதிவிறக்க</span>
              </div>

              <div className="border-t border-2D497E p-5 flex items-center gap-3">
                <Link
                  href="https://apps.apple.com/app/dinamani-tamil-news/id1244532821"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  <Image src="https://media.dinamani.com/dinamani/2026-02-27/73dqc9df/appstore.png" alt="App Store" width={128} height={38} className="w-[128px] h-[38px]" loading="lazy" />
                </Link>
                <Link
                  href="https://play.google.com/store/apps/details?id=com.dinamani.android"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  <Image src="https://media.dinamani.com/dinamani/2026-02-27/gdkfu5wb/googleplay.png" alt="Google Play" width={128} height={38} className="w-[128px] h-[38px]" loading="lazy" />
                </Link>
              </div>
            </div>

            {/* Center column */}
            <div className="lg:border-x border-2D497E">
              {/* Top horizontal nav */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 justify-start lg:justify-start text-FFFFFF text-12-english-500 border-b border-2D497E p-5">
                {footerTopItems.map((item) => {
                  const resolved = resolveFooterHref(item);
                  if (!resolved) return null;
                  return (
                    <FooterLink key={`${item.title}-${resolved.href}`} href={resolved.href} external={resolved.external}>
                      {item.title}
                    </FooterLink>
                  );
                })}
              </div>

              {/* Sections grid */}
              <div className="">
                <div className="flex p-5 border-b border-2D497E items-center gap-2 text-FFFFFF text-12-english-500 uppercase tracking-wide">
                  {/* <FaArrowDown size={16} aria-hidden /> */}
                  <span className="text-12-tamil-900">செய்திப் பிரிவுகள்</span>
                </div>
                <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 lg:grid-cols-3 gap-y-4 gap-x-6 text-FFFFFF text-12-tamil-500">
                  {footerSectionItems.map((item) => {
                    const resolved = resolveSectionHref(item);
                    if (!resolved) return null;
                    return (
                      <FooterLink
                        key={`${item.title}-${resolved.href}`}
                        href={resolved.href}
                        external={resolved.external}
                      >
                        {item.title}
                      </FooterLink>
                    );
                  })}
                </div>
              </div>

              {/* Copyright */}
              <div className="p-5 border-t border-2D497E text-FFFFFF text-12-tamil-500">
                ©2026 தினமணி மற்றும் அதன் அனைத்து உடைமைகளும் பாதுகாப்பில் உள்ளன. தனியுரிமை கொள்கை மற்றும் பயனாளர் விதிமுறைகள்.
              </div>
            </div>

            {/* Right column */}
            <div className="pl-5 lg:pl-8 pt-8 p-5 lg:border-r border-2D497E">
              <div className="text-12-english-500 uppercase tracking-wide" style={{ color: '#FFDB59' }}>The New Indian Express Group</div>
              <ul className="mt-5 space-y-6 text-FFFFFF text-12-english-500">
                {footerGroupPages.map((item) => {
                  const resolved = resolveSectionHref(item);
                  if (!resolved) return null;
                  return (
                    <li key={`${item.title}-${resolved.href}`}>
                      <FooterLink href={resolved.href} external={resolved.external}>
                        {item.title}
                      </FooterLink>
                    </li>
                  );
                })}
              </ul>

              {/* Notification Icons - Grid positioning to match image */}
              <div className="absolute bottom-4 right-4 grid grid-cols-1 gap-3">
                <button
                  aria-label="Notifications"
                  className="inline-flex items-center justify-center w-[40px] h-[40px] rounded-full bg-215EF9 shadow-md hover:shadow-lg transition-shadow"
                >
                  <FaBell size={22} color="#FFFFFF" aria-hidden />
                </button>
                <button
                  aria-label="Scroll to top"
                  className="inline-flex items-center justify-center w-[40px] h-[40px] rounded-full bg-FFDB59 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <FaAngleUp size={22} color="#000000" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
